import type { z } from 'zod';

import { buildGeoTaxonomyMismatchLine } from '@revelio/core/global-ai-assistant/geo-taxonomy-switch';
import type { FeClientToolName } from '@revelio/core/global-ai-assistant/tool-names';

import type { ToolEmit } from '../global-ai-tool-progress';
import type { ArtifactCache } from '../global-ai-turn-messages';
import { applyFiltersTool } from './apply-filters';
import { askUserTool } from './ask-user';
import { createCustomPageTool } from './create-custom-page';
import { navigateTool } from './navigate';
import { offerPageOrCustomTool } from './offer-page-or-custom';
import { offerReportTool } from './offer-report';
import { offerStandardPageTool } from './offer-standard-page';
import { renderChartTool } from './render-chart';
import {
  malformedToolInputResult,
  turnAbortedNoOpResult,
} from './tool-result-copy';
import {
  type ClientToolDefinition,
  type ClientToolDeps,
  type ClientToolNarrationPolicy,
  type ClientToolResult,
  isClientToolFailure,
} from './types';
import { updateCurrentPageTool } from './update-current-page';

/**
 * The FE half of the turn-loop tool pair: exactly the server's
 * `CLIENT_TOOL_NAMES` plus `ask_user`, which the server types as its own kind
 * but the FE loop receives as an ordinary `tool_call`. `satisfies
 * Record<FeClientToolName, …>` against the shared list in
 * `@revelio/core/global-ai-assistant/tool-names` makes the pairing a COMPILE
 * error in both directions — a tool added on one side only used to dead-end at
 * runtime as `ERROR: X is not a client tool`.
 *
 * Lookups go through the derived `Map`, never this object, because `name` is
 * model-authored: `__proto__` / `toString` resolve off an object's prototype
 * chain, pass the guard in `executeClientTool` and then throw on `.schema`.
 * (`strict` also makes a direct `CLIENT_TOOL_DEFINITIONS[name]` a TS7053
 * error, so the compiler backs this up.) `satisfies` checks the rows WITHOUT
 * widening them, so it is the `ReadonlyMap<string, ClientToolDefinition>`
 * annotation below that unifies the nine narrow schemas to `z.ZodTypeAny`
 * — drop it and `definition.run` becomes an uncallable union.
 */
const CLIENT_TOOL_DEFINITIONS = {
  navigate: navigateTool,
  apply_filters: applyFiltersTool,
  render_chart: renderChartTool,
  create_custom_page: createCustomPageTool,
  offer_page_or_custom: offerPageOrCustomTool,
  offer_standard_page: offerStandardPageTool,
  update_current_page: updateCurrentPageTool,
  offer_report: offerReportTool,
  ask_user: askUserTool,
} satisfies Record<FeClientToolName, ClientToolDefinition>;

const CLIENT_TOOL_REGISTRY: ReadonlyMap<string, ClientToolDefinition> = new Map(
  Object.entries(CLIENT_TOOL_DEFINITIONS)
);

/** Names the registry doesn't know — server tools included — take `'answer'`. */
export function clientToolNarrationPolicy(
  name: string
): ClientToolNarrationPolicy {
  return CLIENT_TOOL_REGISTRY.get(name)?.narrationPolicy ?? 'answer';
}

// A junk array yields one zod issue PER MEMBER, and this string re-enters the
// conversation — unbounded, it would pollute the rest of the turn.
const MAX_PARSE_ISSUES_SHOWN = 5;

function describeParseIssues(error: z.ZodError): string {
  const shown = error.issues
    .slice(0, MAX_PARSE_ISSUES_SHOWN)
    .map((issue) =>
      issue.path.length > 0
        ? `${issue.path.join('.')}: ${issue.message}`
        : issue.message
    )
    .join('; ');
  const omitted = error.issues.length - MAX_PARSE_ISSUES_SHOWN;
  return omitted > 0 ? `${shown}; …and ${omitted} more issue(s)` : shown;
}

export async function executeClientTool(
  name: string,
  input: unknown,
  artifacts: ArtifactCache,
  deps: ClientToolDeps,
  // Only create_custom_page emits; the rest ignore it.
  emit?: ToolEmit,
  // The turn's abort signal, threaded into ClientToolContext so awaiting
  // tools can bail without side effects after the turn is stopped (the digest
  // layer resolves on abort rather than rejecting — see the ctx docblock).
  signal?: AbortSignal
): Promise<ClientToolResult> {
  const definition = CLIENT_TOOL_REGISTRY.get(name);
  if (!definition) {
    // The "is not a client tool" wording is load-bearing: `classifyToolError`
    // buckets on it.
    return { resultForModel: `ERROR: ${name} is not a client tool` };
  }
  // The stream envelope leaves `input` as `unknown`
  // (global-ai-turn-step-event.schema.ts): model-authored arguments are
  // narrowed HERE and nowhere earlier. Like the wording above, "invalid input
  // for" is load-bearing for `classifyToolError`.
  const parsed = definition.schema.safeParse(input);
  if (!parsed.success) {
    return malformedToolInputResult(name, describeParseIssues(parsed.error));
  }
  const result = await definition.run(parsed.data, {
    artifacts,
    deps,
    emit,
    signal,
  });
  return withGeoTaxonomyNote(result, parsed.data, artifacts);
}

/**
 * Carry the geography-taxonomy note forward on EVERY tool that consumed a
 * filter artifact.
 *
 * The note originates in the SERVER-side `search_filters` result, which lives
 * only in that step's model messages: a client tool ENDS the step, so by the
 * next one — where the model writes the user-facing narration — the note is
 * gone and loop rule 9c has nothing to fire on. Re-emitting it from the same
 * artifact is what keeps the disclosure and the switch offer alive.
 *
 * Done here, once, rather than per tool: when only `apply_filters` re-emitted
 * it, a headcount ask routed through another artifact consumer lost the note
 * (Texas → State, 2026-09-03). Any tool that takes a `filterArtifactId` is a
 * consumer, present or future.
 *
 * Skipped for a failure (`ERROR` prefix, which stays the contract's first
 * characters either way) and for the stopped-turn no-op: neither is followed
 * by a narration that could disclose anything.
 */
function withGeoTaxonomyNote(
  result: ClientToolResult,
  input: unknown,
  artifacts: ArtifactCache
): ClientToolResult {
  if (result === turnAbortedNoOpResult || isClientToolFailure(result)) {
    return result;
  }
  const filterArtifactId = (input as { filterArtifactId?: unknown })
    ?.filterArtifactId;
  if (typeof filterArtifactId !== 'string') return result;
  const artifact = artifacts.get(filterArtifactId) as
    | { geographyTaxonomyMismatch?: unknown }
    | undefined;
  const note = buildGeoTaxonomyMismatchLine(
    artifact?.geographyTaxonomyMismatch
  );
  if (!note) return result;
  return { ...result, resultForModel: `${result.resultForModel}\n${note}` };
}
